import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CreditCard, Check, Loader2, CalendarClock } from 'lucide-react';
import PaymentHoldOverlay from './PaymentHoldOverlay';
import { useAuth } from '../../contexts/AuthContext';
import { resolveAssetUrl } from '../../lib/assets';
import { isValidPhone, PHONE_ERROR } from '../../lib/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT_CLS = 'bg-[var(--bg-input)] border-[var(--border-input)] text-[var(--text-primary)] placeholder:text-[var(--text-faint)]';
const DEFAULT_SLOTS = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'];

// Self-contained "Pakalpojumi & apmaksa" block for a business page (used by the
// business-card template): a priced service/item list + a date/time booking
// dialog that reuses the SAME payment engine as reservations/menu — online card
// via KoLV (Stripe) or pay on-site/remotely.
export default function ServiceBooking({ userId, pageId, services = [], timeSlots = [], allowOnline = true, allowOnsite = false, pv = {}, order = 99 }) {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(() => new Date());
  const [slot, setSlot] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [taken, setTaken] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [payHold, setPayHold] = useState(null);
  const [payChecking, setPayChecking] = useState(false);

  const slots = timeSlots?.length ? timeSlots : DEFAULT_SLOTS;
  const dISO = date.toISOString().slice(0, 10);
  const todayISO = new Date().toISOString().slice(0, 10);

  const loadAvailability = useCallback(() => {
    axios.get(`${API}/business-page/${userId}/availability`, { params: { date: dISO, page_id: pageId || undefined } })
      .then(({ data }) => setTaken(data.taken || []))
      .catch(() => setTaken([]));
  }, [dISO, userId, pageId]);
  useEffect(() => { if (bookingOpen) loadAvailability(); }, [bookingOpen, loadAvailability]);

  // Returning from Stripe (?paid=1&session_id=...): poll the booking payment.
  useEffect(() => {
    const sid = sp.get('session_id');
    if (sp.get('cancelled')) { toast.error('Maksājums atcelts'); sp.delete('cancelled'); setSp(sp, { replace: true }); return; }
    if (!sid || !sp.get('paid')) return;
    setPayChecking(true);
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/business-page/booking/status/${sid}`);
        if (data.payment_status === 'paid') {
          setPayChecking(false);
          toast.success('Maksājums saņemts! Pieprasījums gaida apstiprinājumu.');
          sp.delete('paid'); sp.delete('session_id'); setSp(sp, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      attempt += 1;
      if (attempt >= 8) {
        setPayChecking(false);
        toast.message('Maksājums tiek apstrādāts', { description: 'Statuss atjaunosies drīzumā.' });
        sp.delete('paid'); sp.delete('session_id'); setSp(sp, { replace: true });
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!services.length) return null;

  const openBooking = (svc) => {
    setService(svc); setConfirmed(false); setSlot(''); setDate(new Date());
    setForm((f) => ({ name: f.name || user?.name || '', phone: f.phone || user?.phone || '', email: f.email || user?.email || '', note: '' }));
    setBookingOpen(true);
  };
  const payRequired = !!(service && Number(service.price) > 0);
  const online = allowOnline !== false;
  const onsite = !!allowOnsite;
  const optStyle = { background: 'var(--bg-surface-secondary)', color: 'var(--text-primary)' };

  const submitBooking = async (payMethod) => {
    if (!slot) { toast.error('Izvēlies laiku'); return; }
    if (!form.name.trim()) { toast.error('Ievadi vārdu'); return; }
    if (!form.phone.trim()) { toast.error('Norādi telefona numuru'); return; }
    if (!isValidPhone(form.phone)) { toast.error(PHONE_ERROR); return; }
    setSubmitting(true);
    try {
      const { data: resp } = await axios.post(`${API}/business-page/${userId}/booking`, {
        page_id: pageId || undefined,
        pay_method: payRequired ? (payMethod || 'online') : undefined,
        service_id: service?.id, service_name: service?.name,
        date: dISO, time_slot: slot, name: form.name, phone: form.phone, email: form.email, note: form.note,
        origin_url: window.location.origin,
      });
      if (resp.requires_payment && resp.checkout_url) {
        setBookingOpen(false);
        setPayHold({ url: resp.checkout_url, until: resp.hold_until ? new Date(resp.hold_until).getTime() : Date.now() + (resp.hold_minutes || 15) * 60000 });
        return;
      }
      toast.success(resp.pay_choice === 'external' ? 'Pieprasījums nosūtīts! Apmaksā klātienē vai attālināti.' : 'Pieprasījums nosūtīts!');
      setConfirmed(true);
      setSlot(''); setForm({ name: '', phone: '', email: '', note: '' });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Neizdevās nosūtīt');
      if (e?.response?.status === 409) { setSlot(''); loadAvailability(); }
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <section data-testid="card-services-section" style={{ order }}>
        <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <CalendarClock className="w-5 h-5 text-[var(--accent)]" /> Pakalpojumi & apmaksa
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {services.map((s, i) => (
            <div key={s.id || i} className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-5 shadow-sm flex flex-col" data-testid={`card-service-${s.id || i}`}>
              {s.image_url && <img src={resolveAssetUrl(s.image_url)} alt="" className="w-full h-36 object-cover rounded-xl mb-3" />}
              <p className="text-[var(--text-primary)] font-semibold">{s.name}</p>
              {s.description && <p className="text-[var(--text-faint)] text-sm mt-1 flex-1">{s.description}</p>}
              <div className="flex items-center justify-between gap-3 mt-4">
                <span className="text-[var(--accent)] font-bold">{Number(s.price) > 0 ? `€${Number(s.price).toFixed(2)}` : 'Bezmaksas'}</span>
                <button onClick={() => openBooking(s)}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-bold hover:bg-[var(--accent-hover)] transition-colors"
                  data-testid={`book-card-service-${s.id || i}`}>
                  <CalendarClock className="w-4 h-4" /> {Number(s.price) > 0 ? 'Rezervēt & maksāt' : 'Rezervēt'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {payHold && <PaymentHoldOverlay url={payHold.url} until={payHold.until} onClose={() => setPayHold(null)} />}

      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-lg" data-testid="card-booking-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Rezervēt laiku</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">Izvēlies datumu un laiku, un mēs nosūtīsim pieprasījumu uzņēmumam.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {confirmed ? (
              <div className="text-center py-4" data-testid="card-booking-confirmation">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center"><Check className="w-7 h-7 text-emerald-400" /></div>
                <p className="text-[var(--text-primary)] font-semibold mb-1">Pieprasījums nosūtīts!</p>
                <p className="text-[var(--text-faint)] text-sm mb-4">Rezervācija gaida uzņēmuma apstiprinājumu. Kad tā tiks apstiprināta, saņemsi apstiprinājuma kodu{form.email ? ' e-pastā' : ''}.</p>
                <button onClick={() => setBookingOpen(false)} className="w-full h-11 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] font-semibold hover:opacity-80" data-testid="card-booking-done">Aizvērt</button>
              </div>
            ) : (
              <>
                {service && <p className="text-[var(--accent)] text-sm font-semibold">{service.name}{Number(service.price) > 0 ? ` · €${Number(service.price).toFixed(2)}` : ''}</p>}
                {payRequired && (
                  <div className="rounded-xl bg-[var(--accent-soft)] border border-[var(--accent-border)] px-3 py-2 text-[var(--text-muted)] text-xs" data-testid="card-booking-payment-note">
                    Cena €{Number(service.price).toFixed(2)}. {online && onsite ? 'Izvēlies, kā maksāt: tagad online ar karti caur KoLV, vai klātienē/attālināti.' : online ? 'Apmaksa online ar karti caur KoLV.' : 'Apmaksa klātienē vai attālināti.'}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={dISO} min={todayISO} onChange={(e) => { if (e.target.value) { setDate(new Date(e.target.value + 'T00:00:00')); setSlot(''); } }}
                    className="h-11 rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-primary)] text-sm px-3 [color-scheme:dark]" data-testid="card-dialog-date" />
                  <select value={slot} onChange={(e) => setSlot(e.target.value)} className="h-11 rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-primary)] text-sm px-3" data-testid="card-dialog-slot-select">
                    <option value="" style={optStyle}>Izvēlies laiku</option>
                    {slots.map((s) => <option key={s} value={s} disabled={taken.includes(s)} style={optStyle}>{s}{taken.includes(s) ? ' — aizņemts' : ''}</option>)}
                  </select>
                </div>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tavs vārds" className={INPUT_CLS} data-testid="card-booking-name" />
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefons *" className={INPUT_CLS} data-testid="card-booking-phone" />
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-pasts (lai saņemtu apstiprinājumu)" className={INPUT_CLS} data-testid="card-booking-email" />
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Piezīme (neobligāti)" className={INPUT_CLS} data-testid="card-booking-note" />
                {payRequired ? (
                  <div className="space-y-2">
                    {online && (
                      <button onClick={() => submitBooking('online')} disabled={submitting}
                        className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="card-submit-booking-online">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Maksāt tagad €{Number(service.price).toFixed(2)}
                      </button>
                    )}
                    {onsite && (
                      <button onClick={() => submitBooking('external')} disabled={submitting}
                        className={`w-full h-11 rounded-full disabled:opacity-60 flex items-center justify-center gap-2 font-semibold ${online ? 'border border-[var(--border-card)] text-[var(--text-primary)] hover:bg-[var(--bg-card)]' : 'bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)]'}`} data-testid="card-submit-booking-external">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Maksāt klātienē / attālināti
                      </button>
                    )}
                  </div>
                ) : (
                  <button onClick={() => submitBooking()} disabled={submitting}
                    className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="card-submit-booking">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Nosūtīt pieprasījumu
                  </button>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {payChecking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm" style={{ background: 'var(--bg-overlay)' }} data-testid="card-pay-checking">
          <div className="flex flex-col items-center gap-3 text-[var(--text-primary)]">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" />
            <p className="text-sm">Apstiprinām maksājumu…</p>
          </div>
        </div>
      )}
    </>
  );
}
